import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Stack, Typography, TextField,
  MenuItem, Button, Grid, Avatar, Autocomplete, CircularProgress, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import StorefrontIcon from '@mui/icons-material/StorefrontRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeRounded';
import UploadFileIcon from '@mui/icons-material/UploadFileRounded';
import PhotoCameraIcon from '@mui/icons-material/PhotoCameraRounded';
import ImageEditorDialog from '@/components/ImageEditorDialog';
import { connectionsPathFor } from '@/utils/connectionsPath';
import { businessesApi } from '@/api/businesses';
import { mediaApi } from '@/api/media';
import { apiError } from '@/api/client';
import { useBusinessStore } from '@/store/businessStore';
import { PALETTE } from '@/theme/tokens';
import { BUSINESS_ICONS, getBusinessIcon } from '@/theme/businessIcons';

// Expanded brand-color palette.
const BRAND_COLORS = [
  '#4F46E5', '#7C3AED', '#EC4899', '#EF4444', '#F97316', '#F59E0B',
  '#EAB308', '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
  '#3B82F6', '#6366F1', '#8B5CF6', '#D946EF', '#F43F5E', '#64748B',
];

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Karachi',
  'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
];
const TONES = ['Professional', 'Friendly', 'Casual', 'Witty', 'Inspirational', 'Bold', 'Luxury', 'Educational'];
const LANGS = ['English', 'Urdu', 'Arabic', 'Spanish', 'German', 'French', 'Hindi'];
const CATEGORIES = [
  // Food & drink
  'Restaurant', 'Fast Food', 'Cafe', 'Coffee Shop', 'Bakery', 'Dessert & Ice Cream', 'Bar & Pub',
  'Catering', 'Food Truck', 'Cloud Kitchen', 'Grocery & Supermarket', 'Food & Beverage',
  // Retail & fashion
  'Retail', 'Boutique', 'Fashion & Apparel', 'Footwear', 'Jewelry & Accessories', 'Watches',
  'Beauty & Cosmetics', 'Perfume & Fragrance', 'Eyewear', 'Toys & Games', 'Bookstore', 'Florist', 'Gift Shop',
  // Beauty & wellness
  'Salon & Spa', 'Barbershop', 'Nail Salon', 'Skincare Clinic', 'Fitness & Gym', 'Yoga & Pilates',
  'Personal Trainer', 'Nutrition & Diet', 'Health & Wellness', 'Massage & Therapy',
  // Health & medical
  'Healthcare & Clinic', 'Dental Clinic', 'Hospital', 'Pharmacy', 'Optician', 'Veterinary', 'Mental Health',
  // Property & home
  'Real Estate', 'Property Management', 'Construction', 'Architecture', 'Interior Design', 'Home Services',
  'Cleaning Services', 'Landscaping & Gardening', 'Furniture & Decor', 'Home Appliances', 'Moving & Storage',
  // Auto & transport
  'Automotive', 'Car Dealership', 'Car Rental', 'Auto Repair', 'Logistics & Delivery', 'Ride Hailing',
  // Travel & hospitality
  'Travel & Tourism', 'Travel Agency', 'Hotel & Hospitality', 'Resort', 'Vacation Rentals', 'Airline',
  // Professional & business
  'Marketing Agency', 'Advertising', 'PR & Communications', 'Consulting', 'Accounting & Bookkeeping',
  'Finance & Insurance', 'Banking', 'Investment', 'Legal Services', 'HR & Recruitment', 'Coworking Space',
  // Tech & digital
  'Technology & SaaS', 'Software Development', 'Mobile Apps', 'IT Services', 'Cybersecurity', 'AI & Data',
  'E-commerce', 'Web Design', 'Gaming', 'Telecom', 'Electronics',
  // Education
  'Education & Training', 'School', 'University & College', 'Online Courses', 'Tutoring', 'Coaching',
  'Language School', 'Daycare & Preschool',
  // Creative & media
  'Photography', 'Videography', 'Graphic Design', 'Content Creator', 'Influencer', 'Music & Band',
  'Film & Production', 'Publishing', 'Podcast',
  // Events & lifestyle
  'Events & Weddings', 'Event Planning', 'Party & Entertainment', 'Nightlife', 'Sports & Recreation',
  'Pet Services', 'Childcare', 'Non-profit & Charity', 'Religious Organization', 'Community',
  // Industry
  'Manufacturing', 'Wholesale & Distribution', 'Agriculture', 'Energy & Utilities', 'Mining', 'Textiles',
  'Chemicals', 'Government', 'Other',
];
const STEPS = ['Basics', 'Brand & products', 'Posting plan'];
const PLANS = [
  { key: 8, label: 'Light', desc: 'A couple of posts a week', per: 8 },
  { key: 16, label: 'Balanced', desc: 'A steady, consistent presence', per: 16 },
  { key: 30, label: 'Active', desc: 'Post almost every day', per: 30 },
  { key: 60, label: 'Aggressive', desc: 'Multiple posts every day', per: 60 },
];
const PLAN_NETWORKS = ['facebook', 'instagram', 'linkedin', 'twitter', 'gmb'];
const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();
const TEXT_FILE = /\.(csv|txt|md|tsv|json)$/i;

export default function CreateBusinessDialog({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setCurrentBusiness = useBusinessStore((s) => s.setCurrentBusiness);
  const [step, setStep] = useState(0);
  const [err, setErr] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeNote, setScrapeNote] = useState('');
  const [logoBusy, setLogoBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [form, setForm] = useState({
    name: '', category: '', timezone: TIMEZONES.includes(browserTz) ? browserTz : 'UTC',
    color: BRAND_COLORS[0], icon: 'storefront', logo: '', website: '', brand_tone: 'Professional',
    preferred_language: 'English', target_audience: '', description: '', products: '',
    posting_goal: 16,
  });
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const reset = () => {
    setStep(0); setErr(''); setScrapeNote('');
    setForm((f) => ({ ...f, name: '', category: '', logo: '', website: '', target_audience: '', description: '', products: '' }));
  };

  // --- Website scrape / autofill ------------------------------------------
  const autofill = async () => {
    const url = form.website.trim();
    if (!url) { setErr('Enter a website first.'); return; }
    setErr(''); setScrapeNote(''); setScraping(true);
    try {
      const res = await businessesApi.scrape(url);
      const d = res?.data || {};
      if (res?.mocked) { setScrapeNote('AI is not configured — could not auto-extract.'); return; }
      update({
        name: form.name || d.name || '',
        category: form.category || d.category || d.industry || '',
        description: form.description || d.description || '',
        target_audience: form.target_audience || d.target_audience || '',
        brand_tone: TONES.includes(d.brand_tone) ? d.brand_tone : form.brand_tone,
        products: form.products || d.products || '',
      });
      setScrapeNote('Filled from your website — review and edit as needed.');
    } catch (e) {
      setErr(apiError(e, 'Could not read that website.'));
    } finally {
      setScraping(false);
    }
  };

  // --- Logo upload (crop first) --------------------------------------------
  const onLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr('');
    setCropSrc(URL.createObjectURL(file)); // open cropper
  };

  const uploadCroppedLogo = async (blob) => {
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, 'logo.jpg');
      const res = await mediaApi.upload(fd);
      const url = (res?.data || res)?.url;
      if (url) update({ logo: url });
    } catch (ex) {
      setErr(apiError(ex, 'Logo upload failed (images only).'));
    } finally {
      setLogoBusy(false);
    }
  };

  const closeCropper = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  // --- Products file (read text files client-side) -------------------------
  const onProductsFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!TEXT_FILE.test(file.name) && !file.type.startsWith('text')) {
      setErr('For docs/PDF, please copy-paste the text. CSV/TXT files can be uploaded directly.');
      return;
    }
    try {
      const t = await file.text();
      update({ products: (form.products ? form.products + '\n' : '') + t.slice(0, 6000) });
      setErr('');
    } catch {
      setErr('Could not read that file.');
    }
  };

  const create = useMutation({
    mutationFn: () => businessesApi.create({
      name: form.name.trim(),
      category: form.category || undefined,
      industry: form.category || undefined,
      timezone: form.timezone,
      logo: form.logo || undefined,
      website: form.website || undefined,
      brand_tone: form.brand_tone || undefined,
      preferred_language: form.preferred_language || undefined,
      target_audience: form.target_audience || undefined,
      description: form.description || undefined,
      brand_color: form.color,
      profile: { color: form.color, icon: form.icon, products: form.products || '', website: form.website || '', posting_goal: form.posting_goal },
    }),
    onSuccess: (res) => {
      const created = res?.data || res;
      qc.invalidateQueries({ queryKey: ['businesses'] });
      // Seed the business posting plan (per-network targets) from the goal.
      if (created?.id && form.posting_goal > 0) {
        try {
          const per = form.posting_goal;
          const base = Math.floor(per / PLAN_NETWORKS.length);
          let rem = per - base * PLAN_NETWORKS.length;
          const map = {};
          PLAN_NETWORKS.forEach((n) => { map[n] = base + (rem-- > 0 ? 1 : 0); });
          localStorage.setItem(`sa_posting_targets_${created.id}`, JSON.stringify(map));
        } catch { /* ignore */ }
      }
      if (created?.id) setCurrentBusiness(created.id);
      onCreated?.(created);
      reset();
      onClose?.();
      // New business → send them to connect social accounts first.
      navigate(connectionsPathFor(created));
    },
    onError: (e) => setErr(apiError(e, 'Could not create business.')),
  });

  const canNext = form.name.trim().length > 0;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3.2, overflow: 'hidden' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1.75 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}>
            <StorefrontIcon fontSize="small" />
          </Box>
          <Box>
            <Typography component="span" sx={{ fontWeight: 800, fontSize: 17, display: 'block', lineHeight: 1.1 }}>New business</Typography>
            <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>Set up a workspace for a new brand</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#FAFBFF' }}>
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mb: 2.5 }}>
          {STEPS.map((label, i) => (
            <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 999, bgcolor: i === step ? PALETTE.primarySoft : 'transparent' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: i <= step ? '#fff' : 'text.disabled', bgcolor: i <= step ? 'primary.main' : PALETTE.divider }}>
                  {i < step ? '✓' : i + 1}
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: i === step ? 800 : 600, color: i === step ? 'primary.main' : 'text.secondary' }}>{label}</Typography>
              </Box>
              {i < STEPS.length - 1 && <Box sx={{ width: 16, height: 2, bgcolor: i < step ? 'primary.main' : PALETTE.divider, borderRadius: 2 }} />}
            </Stack>
          ))}
        </Stack>

        {step === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ position: 'relative' }}>
                  <Avatar src={form.logo || undefined} sx={{ width: 56, height: 56, bgcolor: form.color, fontWeight: 800 }}>
                    {!form.logo && (() => {
                      const I = getBusinessIcon(form.icon);
                      return I ? <I sx={{ fontSize: 26 }} /> : (form.name?.[0] || '?').toUpperCase();
                    })()}
                  </Avatar>
                  <IconButton component="label" size="small" sx={{ position: 'absolute', bottom: -6, right: -6, width: 24, height: 24, bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}>
                    {logoBusy ? <CircularProgress size={13} color="inherit" /> : <PhotoCameraIcon sx={{ fontSize: 14 }} />}
                    <input type="file" accept="image/*" hidden onChange={onLogo} />
                  </IconButton>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Logo <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(optional)</Box></Typography>
                  <Typography variant="caption" color="text.secondary">Upload a square image for this brand</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <TextField autoFocus fullWidth label="Business name" placeholder="e.g. M20 Chain" value={form.name} onChange={(e) => update({ name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={CATEGORIES}
                value={form.category}
                onChange={(_e, v) => update({ category: v || '' })}
                onInputChange={(_e, v) => update({ category: v })}
                renderInput={(params) => <TextField {...params} label="Category" placeholder="Search or type a custom one" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Time zone" value={form.timezone} onChange={(e) => update({ timezone: e.target.value })}>
                {TIMEZONES.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Brand color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {BRAND_COLORS.map((c) => (
                  <Box key={c} onClick={() => update({ color: c })} sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: c, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: form.color === c ? `0 0 0 2px #fff, 0 0 0 3px ${c}` : 'none' }}>
                    {form.color === c && <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />}
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Icon</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {BUSINESS_ICONS.map(({ key, Icon }) => {
                  const active = form.icon === key;
                  return (
                    <Box
                      key={key}
                      onClick={() => update({ icon: key })}
                      sx={{
                        width: 36, height: 36, borderRadius: 2, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: active ? form.color : `${PALETTE.divider}`,
                        color: active ? '#fff' : 'text.secondary',
                        border: active ? `2px solid ${form.color}` : `1px solid ${PALETTE.divider}`,
                        transition: 'all .12s', '&:hover': { transform: 'translateY(-1px)' },
                      }}
                    >
                      <Icon sx={{ fontSize: 19 }} />
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        )}

        {step === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Website" placeholder="yourbrand.com"
                value={form.website}
                onChange={(e) => update({ website: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={autofill} disabled={scraping || !form.website.trim()} startIcon={scraping ? <CircularProgress size={14} /> : <AutoAwesomeIcon />} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {scraping ? 'Reading…' : 'Autofill'}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              {scrapeNote && <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>{scrapeNote}</Typography>}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Brand tone" value={form.brand_tone} onChange={(e) => update({ brand_tone: e.target.value })}>
                {TONES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Content language" value={form.preferred_language} onChange={(e) => update({ preferred_language: e.target.value })}>
                {LANGS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Target audience" placeholder="Who are you speaking to?" value={form.target_audience} onChange={(e) => update({ target_audience: e.target.value })} multiline minRows={2} />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Products / services info</Typography>
                <Button component="label" size="small" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Upload CSV / text
                  <input type="file" accept=".csv,.txt,.md,.tsv,.json,text/*" hidden onChange={onProductsFile} />
                </Button>
              </Stack>
              <TextField
                fullWidth multiline minRows={3}
                placeholder="Paste product names, descriptions and prices — or upload a CSV/text file. Used to make AI posts more accurate."
                value={form.products}
                onChange={(e) => update({ products: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description (optional)" value={form.description} onChange={(e) => update({ description: e.target.value })} multiline minRows={2} />
            </Grid>
          </Grid>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Posting plan</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How often do you want to post? We&apos;ll set a monthly goal for this business (you can fine-tune per-network targets later).
            </Typography>
            <Grid container spacing={1.5}>
              {PLANS.map((p) => {
                const active = form.posting_goal === p.per;
                return (
                  <Grid item xs={6} key={p.key}>
                    <Box
                      onClick={() => update({ posting_goal: p.per })}
                      sx={{
                        cursor: 'pointer', p: 1.75, borderRadius: 2.5, height: '100%',
                        border: `1.5px solid ${active ? PALETTE.primary : PALETTE.divider}`,
                        bgcolor: active ? PALETTE.primarySoft : '#fff', transition: 'all .15s',
                        '&:hover': { borderColor: PALETTE.primary },
                      }}
                    >
                      <Stack direction="row" alignItems="baseline" spacing={0.75}>
                        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{p.label}</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: 15, color: 'primary.main' }}>{p.per}</Typography>
                        <Typography variant="caption" color="text.secondary">/mo</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{p.desc}</Typography>
                    </Box>
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <TextField
                  type="number" size="small" label="Custom posts / month"
                  value={form.posting_goal}
                  onChange={(e) => update({ posting_goal: Math.max(0, Math.min(120, parseInt(e.target.value, 10) || 0)) })}
                  inputProps={{ min: 0, max: 120 }}
                  sx={{ width: 200 }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {err && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1.5 }}>{err}</Typography>}

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2.5 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} sx={{ textTransform: 'none', fontWeight: 700, visibility: step === 0 ? 'hidden' : 'visible' }}>Back</Button>
          {isLast ? (
            <Button variant="contained" startIcon={<StorefrontIcon />} disabled={!canNext || create.isPending} onClick={() => create.mutate()} sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
              {create.isPending ? 'Creating…' : 'Create business'}
            </Button>
          ) : (
            <Button variant="contained" endIcon={<ArrowForwardIcon />} disabled={!canNext} onClick={() => setStep((s) => s + 1)} sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>Next</Button>
          )}
        </Stack>
      </DialogContent>

      <ImageEditorDialog
        open={!!cropSrc}
        src={cropSrc}
        fileName="logo"
        mode="logo"
        onClose={closeCropper}
        onCropped={(blob) => { uploadCroppedLogo(blob); closeCropper(); }}
      />
    </Dialog>
  );
}
